import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { PhuketSegment } from "@/lib/phuket-segments";
import { SERVICES } from "@/lib/services";
import { PHUKET_AREA_PAGES } from "@/lib/phuket-area-pages";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { PageGallery } from "@/components/PageGallery";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { pageHeadForProvince } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/for/$segment")({
  loader: async ({ params }): Promise<{ seg: PhuketSegment; longform?: PageContent }> => {
    const { PHUKET_SEGMENTS } = await import("@/lib/phuket-segments");
    const seg = PHUKET_SEGMENTS.find((x) => x.slug === params.segment);
    if (!seg) throw notFound();
    const { getSegmentLongform } = await import("@/content/longform-segments");
    return { seg, longform: getSegmentLongform(seg.slug) };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.seg;
    if (!s) return { meta: [{ title: "Segment not found" }] };
    const base = pageHeadForProvince({
      title: s.metaTitle,
      description: s.metaDesc,
      path: `/for/${s.slug}`,
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/for/${s.slug}`)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บริการเฉพาะกลุ่ม", url: "/for" },
              { name: s.titleTh, url: `/for/${s.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: SegmentPage,
  notFoundComponent: () => (
    <div className="p-10 text-center"><h1 className="text-2xl font-bold">ไม่พบกลุ่มนี้</h1><Link to="/for" className="text-gold">← กลับ</Link></div>
  ),
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error("Segment page error:", error);
    return (
      <div className="p-10 text-center">
        <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
        <p className="mt-2 text-sm text-muted-foreground">ไม่สามารถโหลดหน้าได้ กรุณาลองใหม่อีกครั้ง</p>
        <a href="/for" className="mt-4 inline-block text-gold">← กลับ</a>
      </div>
    );
  },
});

function SegmentPage() {
  const { seg, longform } = Route.useLoaderData();
  const services = SERVICES.filter((s) => seg.services.includes(s.slug) || seg.services.includes(s.id));
  const areas = PHUKET_AREA_PAGES.filter((a) => seg.areas.includes(a.slug));
  return (
    <ProvincePageShell
      eyebrow={`${seg.emoji} ${seg.titleEn}`}
      title={seg.hero.headline}
      subtitle={seg.hero.sub}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "บริการเฉพาะกลุ่ม", to: "/for" }, { label: seg.titleTh }]}
    >
      <div className="mx-auto max-w-7xl space-y-14 px-4 py-16">
        <section>
          <h2 className="text-2xl font-black text-primary">ปัญหาที่กลุ่ม {seg.titleTh} พบบ่อย</h2>
          <ul className="mt-4 grid gap-3 md:grid-cols-2">
            {seg.pains.map((p: string) => (
              <li key={p} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-black text-primary">บริการแนะนำสำหรับคุณ</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link key={s.id} to="/services/$service" params={{ service: s.slug }} className="group rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold">
                <h3 className="text-base font-bold text-primary group-hover:text-gold">{s.title}</h3>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gold">{s.titleEn}</div>
                <p className="mt-2 text-xs text-muted-foreground">{s.desc}</p>
                {s.priceFrom && <div className="mt-3 text-sm font-bold text-gold">เริ่ม {s.priceFrom.toLocaleString()} บาท</div>}
              </Link>
            ))}
          </div>
        </section>

        {seg.visas.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary">ประเภทวีซ่าที่เกี่ยวข้อง</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {seg.visas.map((v: string) => (
                <span key={v} className="rounded-md border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold">{v}</span>
              ))}
            </div>
          </section>
        )}

        <PageGallery
          category="general"
          seed={seg.slug}
          locale="th"
          title={`ตัวอย่างงานจริงสำหรับกลุ่ม ${seg.titleTh}`}
        />

        <section>
          <h2 className="text-2xl font-black text-primary">พื้นที่ที่ {seg.titleTh} อาศัย/ทำงานบ่อย</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {areas.map((a) => (
              <Link key={a.slug} to="/areas/$area" params={{ area: a.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">{a.districtTh}</div>
                <h3 className="mt-1 text-sm font-bold text-primary group-hover:text-gold">{a.nameTh} — {a.nameEn}</h3>
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูพื้นที่ <ChevronRight className="h-3 w-3" /></div>
              </Link>
            ))}
          </div>
        </section>

        {seg.faq.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary">คำถามที่พบบ่อย</h2>
            <div className="mt-4 space-y-3">
              {seg.faq.map((f: { q: string; a: string }) => (
                <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-bold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>
      {longform && <LongFormContent content={longform} />}
    </ProvincePageShell>
  );
}